/* First-visit storage choice.
 *
 * Neutrally named for the reason set out in lib/preferences.ts: a component file
 * or element id containing "cookie", "consent" or "tracking" is matched by common
 * ad-blocker filter lists, and a banner that never renders is worse than none --
 * it would look, from our side, as though everyone had been asked.
 *
 * Design constraints here are legal rather than aesthetic:
 *  - Reject is ONE click and sits at the same level as Accept. A reject buried
 *    behind a settings dialog is not freely given consent (UK GDPR Art. 7(4)).
 *  - Nothing is pre-ticked; analytics stays denied until a positive choice.
 *  - Dismissing without choosing is not consent, so there is no X that quietly
 *    means yes: the bar stays until a choice is made.
 */
import React, { useEffect, useState } from 'react';

import { acceptAll, hasDecided, rejectNonEssential } from '../lib/preferences';

export default function NoticeBar() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShow(!hasDecided()), 400);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  const decide = (fn: () => unknown) => {
    fn();
    setShow(false);
  };

  const button: React.CSSProperties = {
    font: 'inherit',
    fontWeight: 600,
    fontSize: '0.9rem',
    padding: '0.5rem 1rem',
    borderRadius: '999px',
    border: '1px solid rgba(0,0,0,0.15)',
    background: '#fff',
    color: '#222',
    cursor: 'pointer',
  };

  return (
    <div
      role="dialog"
      aria-label="Your storage choices"
      style={{
        position: 'fixed',
        left: '1rem',
        right: '1rem',
        bottom: 'calc(1rem + env(safe-area-inset-bottom))',
        zIndex: 9000,
        margin: '0 auto',
        maxWidth: '40rem',
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '16px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
        padding: '1.1rem 1.25rem',
        display: 'flex',
        gap: '1rem',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <p style={{ margin: 0, flex: '1 1 17rem', fontSize: '0.9rem', color: '#555', lineHeight: 1.5 }}>
        The sign-in session is essential and stays either way. We'd also like to use Google
        Analytics to see which parts of Seriously get used. Only if you allow it.
      </p>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button style={button} onClick={() => decide(rejectNonEssential)}>
          No thanks
        </button>
        <button
          style={{ ...button, background: '#c0392b', borderColor: '#c0392b', color: '#fff' }}
          onClick={() => decide(acceptAll)}
        >
          Allow
        </button>
      </div>
    </div>
  );
}
